"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, PhoneCall, CircleCheck as CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const trustPoints = [{
  id: "tp-1",
  title: "Fixed-price proposals",
  description: "Clear itemized scopes before work begins"
}, {
  id: "tp-2",
  title: "Dedicated project manager",
  description: "Daily communication and on-site oversight"
}, {
  id: "tp-3",
  title: "Clear written scope",
  description: "Detailed specifications with transparent milestones"
}];
export default function StartProject() {
  const shouldReduceMotion = useReducedMotion();
  const motionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  return <section id="start-your-bathroom-project" data-nav="light" className="relative w-full bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative overflow-hidden rounded-2xl border border-border bg-card/85 p-8 shadow-xl backdrop-blur-md sm:p-12 lg:p-16">
          {/* Subtle architectural hairline accent */}
          <div className="absolute top-0 left-0 h-1 w-32 bg-primary sm:w-48" />

          <div className="mx-auto max-w-3xl text-center">
            {/* Tag Badge */}
            <motion.div variants={motionVariants} className="mb-4 flex justify-center">
              <Badge variant="secondary" className="border border-border/80 px-3 py-1 text-xs font-semibold tracking-wider uppercase text-secondary-foreground">
                Decatur Residential Remodeling
              </Badge>
            </motion.div>

            {/* Heading */}
            <motion.h2 variants={motionVariants} className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Ready to design your new bathroom?
            </motion.h2>

            {/* Supporting Paragraph */}
            <motion.p variants={motionVariants} className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
              Schedule an on-site consultation to walk through layouts, fixtures, and custom tile work with our local Decatur team. We handle design, permits, and construction from start to finish.
            </motion.p>

            {/* Trust Points Grid */}
            <motion.div variants={motionVariants} className="my-8 grid grid-cols-1 gap-4 border-y border-border/70 py-6 text-left sm:grid-cols-3 sm:gap-6 sm:py-8">
              {trustPoints.map((point, index) => <div key={point.id} data-index={index} className="flex items-start gap-3 rounded-lg p-2 transition-[background-color] hover:bg-secondary/40">
                  <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                    <CheckCircle2 className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans text-sm font-semibold text-foreground">
                      {point.title}
                    </h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {point.description}
                    </p>
                  </div>
                </div>)}
            </motion.div>

            {/* Call to Action Buttons */}
            <motion.div variants={motionVariants} className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
              <Button asChild size="lg" className="w-full bg-primary font-sans font-semibold text-primary-foreground shadow-md transition-[color,background-color,border-color,box-shadow] hover:bg-primary/90 hover:shadow-lg sm:w-auto">
                <Link to="#" className="inline-flex items-center gap-2">
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full border-border bg-card/90 font-sans font-semibold text-foreground shadow-xs transition-[color,background-color,border-color,box-shadow] hover:bg-secondary hover:text-foreground sm:w-auto">
                <a href="tel:+14708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4 text-muted-foreground" />
                  <span>(470) 847-9076</span>
                </a>
              </Button>
            </motion.div>

            {/* Micro reassurance copy */}
            <motion.p variants={motionVariants} className="mt-6 text-xs text-muted-foreground">
              Serving Oakhurst, Winnona Park, Great Lakes, and surrounding Decatur neighborhoods.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>;
}